import React, { useEffect, useMemo } from "react";
import { Box, Checkbox, Chip, Paper, Typography } from "@mui/material";
import { splitFromPolicy } from "../utils/companyRules";
import { changedByPart, resubmitPlan, resubmitLabel } from "../utils/partChanges";
import { insurerColor, partStatusMeta, shortLabel } from "../utils/policyParts";

const NAVY = "#1E3A5F";

const rupees = (v) =>
  `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** "what changed" for one part. */
const Changes = ({ change }) => {
  if (!change || !change.changed) {
    return (
      <Typography sx={{ fontSize: 12, color: "#667085" }}>No change for this part</Typography>
    );
  }
  return (
    <Box>
      {change.fields.map((f) => (
        <Typography key={f.label} sx={{ fontSize: 12, color: "#344054", overflowWrap: "anywhere" }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {f.label}:
          </Box>{" "}
          {f.from} → {f.to}
        </Typography>
      ))}
      {change.overflow > 0 && (
        <Typography sx={{ fontSize: 12, color: "#667085" }}>+{change.overflow} more</Typography>
      )}
      {change.premium && (
        <Typography sx={{ fontSize: 12, color: "#344054" }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Premium:
          </Box>{" "}
          {rupees(change.premium.from)} → {rupees(change.premium.to)}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Last step of an edit: which parts (OD / TP / PA) go back to the insurer.
 *
 * Bought parts are locked (never bought twice), parts not started run anyway,
 * and failed / stopped ones are ticked by default when this edit changed
 * something that part uses. The chosen keys are written to the form as
 * `resubmitParts` (see useOnlinePolicyForm) and the operator's own ticks are
 * kept as `_partOverrides` so they survive going back a step.
 *
 * @param {Array}    parts             the policy's parts
 * @param {object}   savedPolicy       the policy as saved
 * @param {object}   values            the form's values
 * @param {object}   newSplit          premium split of the edited data (may be null)
 * @param {object}   vehicle           selected make / model (names for "what changed")
 * @param {boolean}  paymentLink       unpaid payment-link policy (motor parts tickable)
 * @param {object}   overrides         { [partKey]: boolean }
 * @param {Function} onOverridesChange called with the next overrides
 * @param {Function} onKeysChange      called with the keys to re-submit
 */
const ResubmitPartsPanel = ({
  parts,
  savedPolicy,
  values,
  newSplit = null,
  vehicle = null,
  paymentLink = false,
  overrides = {},
  onOverridesChange,
  onKeysChange,
}) => {
  const savedSplit = useMemo(() => splitFromPolicy(savedPolicy), [savedPolicy]);

  const changes = useMemo(
    () =>
      changedByPart({
        savedPolicy,
        newValues: {
          ...values,
          vehicleMake: vehicle?.make ?? vehicle?.VehicleMake ?? values?.vehicleMake,
          vehicleModel: vehicle?.model ?? vehicle?.VehicleModel ?? values?.vehicleModel,
        },
        parts,
        savedSplit,
        newSplit,
      }),
    [savedPolicy, values, vehicle, parts, savedSplit, newSplit]
  );

  const plan = useMemo(
    () => resubmitPlan({ parts, changes, overrides, paymentLink }),
    [parts, changes, overrides, paymentLink]
  );

  // Hand the choice to the form so the payload and the Update button see it.
  const keySignature = plan.keys.join(",");
  useEffect(() => {
    if (onKeysChange) onKeysChange(plan.keys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySignature]);

  if (!plan.rows.length) return null;
  const label = resubmitLabel(plan.keys, parts);

  return (
    <Paper
      elevation={0}
      sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, border: "1px solid #EAECF0", borderRadius: 2 }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: NAVY }}>Re-submit parts</Typography>
      <Typography sx={{ fontSize: 12.5, color: "#667085", mb: 1 }}>
        Choose what goes back to the insurer. A part that is already bought is never bought twice.
      </Typography>

      {plan.rows.map((row, i) => {
        const { part } = row;
        const meta = partStatusMeta(part);
        const insurer = insurerColor(part.company).main;
        return (
          <Box
            key={part.key}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              py: 1,
              borderTop: i === 0 ? "1px solid #EAECF0" : "1px dashed #EAECF0",
              opacity: row.locked && !row.checked ? 0.85 : 1,
            }}
          >
            <Checkbox
              size="small"
              checked={!!row.checked}
              disabled={row.locked}
              onChange={(e) =>
                onOverridesChange && onOverridesChange({ ...overrides, [part.key]: e.target.checked })
              }
              inputProps={{ "aria-label": `Re-submit ${part.label}` }}
              sx={{ mt: -0.25, color: NAVY, "&.Mui-checked": { color: NAVY } }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.25 }}>
                <Typography component="span" sx={{ fontWeight: 700, fontSize: 14, color: NAVY }}>
                  {part.label || shortLabel(part.key)}
                </Typography>
                {part.company && (
                  <Typography component="span" sx={{ fontWeight: 600, fontSize: 13, color: insurer }}>
                    {part.company}
                  </Typography>
                )}
                <Chip
                  label={meta.label}
                  size="small"
                  sx={{ height: 20, fontSize: 11, fontWeight: 600, color: meta.color, backgroundColor: meta.bg }}
                />
              </Box>
              {row.state === "bought" ? (
                <Typography sx={{ fontSize: 12, color: "#667085" }}>{row.reason}</Typography>
              ) : row.state === "with" ? (
                <Typography sx={{ fontSize: 12, color: "#667085" }}>
                  {row.reason}
                  {row.checked ? " — re-submitted with it" : " — not re-submitted"}
                </Typography>
              ) : row.state === "running" ? (
                <Typography sx={{ fontSize: 12, color: "#667085" }}>{row.reason}</Typography>
              ) : (
                <>
                  <Changes change={changes[part.key]} />
                  <Typography sx={{ fontSize: 11.5, color: "#98A2B3", mt: 0.25 }}>
                    {row.reason}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        );
      })}

      <Box
        sx={{
          mt: 1,
          p: 1,
          borderRadius: 1.5,
          backgroundColor: plan.keys.length ? "#F0F9FF" : "#F9FAFB",
          border: `1px solid ${plan.keys.length ? "#B9E6FE" : "#EAECF0"}`,
        }}
      >
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#344054" }}>{label.summary}</Typography>
      </Box>
    </Paper>
  );
};

export default ResubmitPartsPanel;
